import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixHeadlessV1RedirectSessionServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www._base_domain_': [
      {
        srcPath: '/_api/redirects-api',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/redirects-api',
        destPath: '',
      },
      {
        srcPath: '/redirect-session',
        destPath: '',
      },
      {
        srcPath: '/headless/v1/redirect-session',
        destPath: '/v1/redirect-session',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/headless/v1/redirect-session',
        destPath: '/v1/redirect-session',
      },
    ],
    _: [
      {
        srcPath: '/headless/v1/redirect-session',
        destPath: '/v1/redirect-session',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_redirects_redirects';

/**
 * Creates a URL for redirecting a visitor from an external client site to a Wix page for Wix-managed functionality.
 *
 * The Create Redirect Session method enables your external Wix Headless client site, built on any platform, to integrate Wix-managed frontend functionality for specific processes.
 * For example, your site can temporarily redirect a visitor to Wix for authentication, or for a checkout process for bookings, eCommerce, events, or paid plans transactions.
 *
 * To initiate a redirect session:
 *
 * 1. Call Create Redirect Session with the details required for Wix to take care of one specific process (for example, authentication or a bookings checkout). Provide one or more callback URLs, so Wix can redirect the visitor back to your site as appropriate when the process is over.
 * 1. Redirect your visitor to the URL provided in the response. This URL includes query parameters informing Wix where to redirect the visitor back to on your external site.
 * 1. Make sure the pages at the callback URLs you provided take care of the next stages in your visitor flow.
 *
 * > **Note**: When creating a redirect session for a multilingual site:
 * >
 * > * If you don't specify the `lang` parameter in `additional_query_parameters` and the request contains an `x-wix-linguist` header, the language from the header will be added to the redirect URL as `lang`.
 * > * If you specify the `lang` parameter, it takes priority over the header.
 */
export function createRedirectSession(
  payload: object
): RequestOptionsFactory<any> {
  function __createRedirectSession({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.headless.v1.redirect_session',
      method: 'POST' as any,
      methodFqn: 'wix.headless.v1.RedirectSessionService.CreateRedirectSession',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHeadlessV1RedirectSessionServiceUrl({
        protoPath: '/v1/redirect-session',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __createRedirectSession;
}
